import { useMemo, useState } from 'react';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';
import Pagination from './Pagination';
import type { InventorySkuRow } from '../api/types';
import DrillCell from './DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

interface Props {
  rows: InventorySkuRow[];
  /** True when at least one row has cost data — used to hide cost columns
   *  for staff (whose payload has them stripped). */
  showCost: boolean;
}

type SortKey = 'gmroi' | 'margin' | 'on_hand' | 'days_idle';

export default function InventorySkuTable({ rows, showCost }: Props) {
  const [sortBy, setSortBy] = useState<SortKey>('gmroi');

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'gmroi':    return (b.gmroi ?? -Infinity) - (a.gmroi ?? -Infinity);
        case 'margin':   return (b.margin90_mxn ?? 0) - (a.margin90_mxn ?? 0);
        case 'on_hand':  return b.on_hand - a.on_hand;
        case 'days_idle':return (b.days_since_last_sale ?? -1) - (a.days_since_last_sale ?? -1);
      }
    });
    return arr;
  }, [rows, sortBy]);

  const pg = usePagination(sorted);

  if (rows.length === 0) {
    return <Card><Text>{t.inventory.empty}</Text></Card>;
  }

  const headerBtn = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => setSortBy(key)}
      className={'hover:underline ' + (sortBy === key ? 'text-tremor-brand font-semibold' : '')}
    >
      {label}
    </button>
  );

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.inventory.cols.part}</TableHeaderCell>
            <TableHeaderCell>{t.inventory.cols.pdesc}</TableHeaderCell>
            <TableHeaderCell>{t.inventory.cols.sid}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.onHand, 'on_hand')}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{t.inventory.cols.invCost}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.margin, 'margin')}</TableHeaderCell>
            )}
            <TableHeaderCell className="text-right">{t.inventory.cols.units}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.gmroi, 'gmroi')}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell>{t.inventory.cols.abc}</TableHeaderCell>
            )}
            <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.dead, 'days_idle')}</TableHeaderCell>
            <TableHeaderCell>{t.inventory.cols.kind}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pg.pageRows.map((r) => (
            <TableRow key={`${r.line}-${r.part}-${r.sid}`}>
              <TableCell className="font-mono">
                <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>
                  {r.part}
                </DrillCell>
              </TableCell>
              <TableCell>{r.pdesc}</TableCell>
              <TableCell>{r.sid}</TableCell>
              <TableCell className="text-right">{r.on_hand}</TableCell>
              {showCost && (
                <TableCell className="text-right">{r.inv_cost_mxn !== undefined ? fmtMxn.format(r.inv_cost_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{r.margin90_mxn !== undefined ? fmtMxn.format(r.margin90_mxn) : '—'}</TableCell>
              )}
              <TableCell className="text-right">{r.units90}</TableCell>
              {showCost && (
                <TableCell className="text-right">{r.gmroi == null ? '—' : fmtPct.format(r.gmroi)}</TableCell>
              )}
              {showCost && (
                <TableCell>{r.abc_class ?? '—'}</TableCell>
              )}
              <TableCell className="text-right">
                {r.days_since_last_sale == null ? '—' : `${r.days_since_last_sale}d`}
              </TableCell>
              <TableCell>
                {r.kind === 'dead' ? <Badge color="red">{t.inventory.kindBadge.dead}</Badge> :
                 r.kind === 'low'  ? <Badge color="amber">{t.inventory.kindBadge.low}</Badge> :
                                     <Badge color="green">{t.inventory.kindBadge.normal}</Badge>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination {...pg} className="px-4" />
    </Card>
  );
}
